// The themes table through v1: reading every row into the app, and the Themes page's writes. An edit shows on the page
// at once and saves a moment later, each theme's changes gathered into one write.
import type { components } from '@/generated/contracts/api-v1';
import { toast } from '@/components/cms/ui/sonner';
import { toResult, v1 } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { getTheme, getThemeRows, isFixedTheme, setTheme, setThemeRows, themeGroups, type ThemeRow } from '@/modules/theme';

type ThemeDto = components['schemas']['ThemeDto'];
type ThemeChanges = Partial<Pick<ThemeRow, 'label' | 'description' | 'mode' | 'tokens'>>;

const SAVE_DELAY_MS = 300;

const toRow = (dto: ThemeDto): ThemeRow => ({
  uniqueId: dto.uniqueId,
  key: dto.key,
  label: dto.label,
  description: dto.description ?? null,
  mode: dto.mode,
  family: dto.family,
  visible: dto.visible,
  sortOrder: dto.sortOrder,
  tokens: dto.tokens,
});

// Each theme's changes not yet written, by its uniqueId
const pending = new Map<string, { changes: ThemeChanges; timer?: ReturnType<typeof setTimeout> }>();
// Each theme's changes being written now
const writing = new Map<string, ThemeChanges>();
// Goes up with every edit, so a read that began before one does not replace it; such a read is made again once all is saved
let edits = 0;
let readAgain = false;

/** Reads every theme in the table into the app; the rows the app has stay when the read fails, and the defaults when the table is empty. */
export async function loadThemes(): Promise<void> {
  const startedAt = edits;
  const { data, error } = await listAll(page => v1.GET('/api/v1/themes', { params: { query: page } }));
  if (error) {
    // A backend without the themes table answers 404: the app keeps its rows, as it did before themes were data
    if (error.status !== 404) console.warn('[themes] could not read the themes table', error);
    return;
  }
  if (edits !== startedAt) {
    readAgain = true;
    // The edit may be saved already, with nothing left to call settled()
    settled();
    return;
  }
  // Edits not yet saved stay over what the table holds
  const unsaved = (row: ThemeRow): ThemeRow =>
    row.uniqueId ? { ...row, ...writing.get(row.uniqueId), ...pending.get(row.uniqueId)?.changes } : row;
  setThemeRows(data.map(toRow).map(unsaved));
}

// Everything saved: a read skipped for an edit is made now
function settled() {
  if (!readAgain || pending.size > 0 || writing.size > 0) return;
  readAgain = false;
  void loadThemes();
}

const failed = (what: string, error: unknown) => {
  console.error(`[themes] ${what}`, error);
  toast.error(`The theme could not be ${what}; it shows as the table has it.`);
  void loadThemes();
};

async function write(uniqueId: string) {
  const entry = pending.get(uniqueId);
  if (!entry) return;
  clearTimeout(entry.timer);
  pending.delete(uniqueId);
  writing.set(uniqueId, { ...writing.get(uniqueId), ...entry.changes });
  const { error } = await toResult(v1.PATCH('/api/v1/themes/{themeUniqueId}', { params: { path: { themeUniqueId: uniqueId } }, body: entry.changes }));
  writing.delete(uniqueId);
  if (error) failed('saved', error);
  settled();
}

/**
 * Changes a theme other than Lureo's fixed Light and Dark: on the page at once, saved a moment later. Its mode changes
 * only when the edit sets one.
 */
export function editTheme(theme: ThemeRow, edit: ThemeChanges) {
  const { uniqueId } = theme;
  if (!uniqueId || isFixedTheme(theme)) return;
  const changes: ThemeChanges = edit;
  edits += 1;
  setThemeRows(getThemeRows().map(row => (row.key === theme.key ? { ...row, ...changes } : row)));
  const entry = pending.get(uniqueId);
  clearTimeout(entry?.timer);
  pending.set(uniqueId, { changes: { ...entry?.changes, ...changes }, timer: setTimeout(() => void write(uniqueId), SAVE_DELAY_MS) });
}

/** A key no theme has, from a label: lowercase words joined by hyphens, numbered when taken. */
function keyFor(label: string): string {
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 56) || 'theme';
  const taken = new Set(getThemeRows().map(row => row.key));
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}-${n}`;
  return key;
}

/**
 * A new theme with `from`'s family, tokens and blurb (not Light's or Dark's, which describe themselves), its mode from its
 * surface, and put on.
 */
export async function createTheme(from: ThemeRow, label: string) {
  const key = keyFor(label);
  const sortOrder = Math.max(0, ...getThemeRows().map(row => row.sortOrder)) + 1;
  const { error } = await toResult(
    v1.POST('/api/v1/themes', {
      body: { uniqueId: crypto.randomUUID(), key, label, description: isFixedTheme(from) ? null : from.description, mode: from.mode, family: from.family, visible: true, sortOrder, tokens: from.tokens },
    }),
  );
  if (error) return failed('made', error);
  await loadThemes();
  setTheme(key);
}

/** Deletes a theme other than Lureo's fixed Light and Dark, putting on the one before it in the list. */
export async function deleteTheme(theme: ThemeRow) {
  if (!theme.uniqueId || isFixedTheme(theme)) return;
  const order = themeGroups(getThemeRows()).flatMap(group => group.themes.map(row => row.key));
  const at = order.indexOf(theme.key);
  const next = order[at > 0 ? at - 1 : at + 1];
  pending.delete(theme.uniqueId);
  const { error } = await toResult(v1.DELETE('/api/v1/themes/{themeUniqueId}', { params: { path: { themeUniqueId: theme.uniqueId } } }));
  if (error) return failed('deleted', error);
  if (next && getTheme() === theme.key) setTheme(next);
  await loadThemes();
}
